import {useSelector} from "react-redux";
import {useParams} from "react-router";
import {css, Theme} from "@emotion/react";
import {capitalizeFirst} from "@rw/nodash";

import {IStore} from "../../../app/reducers/hybrid_reducer";
import {getVoivodeshipName, Voivodeship} from "../../../app/types/locationTypes";
import {City} from "../actions/fetch_location_car_list_data_at_route";
import {parseOfferListPath, toModelSlug} from "../url_utils/parse_offer_list_path";
import {formatMakeDisplayName} from "../utils/format_make_display_name";
import {formatModelDisplayName} from "../utils/format_model_display_name";
import {pluralListing} from "../utils/list_location_copy";

/** Build list path with optional model segment — avoids path-to-regexp dropping params. */
const buildListHref = (parts: {offerType?: string; type?: string; model?: string; location?: string; subLocation?: string}): string => {
    const offerType = parts.offerType || "osobowe";
    const type = parts.type ? String(parts.type).toLowerCase() : "";
    const model = parts.model ? toModelSlug(parts.model) : "";
    const location = parts.location ? String(parts.location).toLowerCase() : "";
    const subLocation = parts.subLocation ? String(parts.subLocation).toLowerCase() : "";

    const segments = [offerType, type, model, location, subLocation].filter(Boolean);
    return `/${segments.join("/")}/`;
};

export const SeoLocationLinks = () => {
    const topRegions = useSelector((store: IStore) => store.carList.carsTopLocations);
    const locationRegions = useSelector((store: IStore) => store.carList.carsLocations);
    const latestQuery = useSelector((store: IStore) => store.carList.latestQuery);
    const routeParams = useParams<{
        type?: string;
        location?: string;
        subLocation?: string;
        subSubLocation?: string;
    }>();
    const parsed = parseOfferListPath(routeParams || {});
    const makeSlug = (parsed.type || latestQuery.type || "").toString().toLowerCase() || undefined;
    const makeType = formatMakeDisplayName(makeSlug || latestQuery.type);
    const modelSlug = parsed.model || (latestQuery as {model?: string}).model || undefined;
    const modelLabel = formatModelDisplayName(modelSlug);
    const makeModelLabel = [makeType, modelLabel].filter(Boolean).join(" ");
    const offerType = (latestQuery.offer_type as string) || "osobowe";
    const currentCitySlug = parsed.subLocation?.toLowerCase();
    const citiesWithOffers = (locationRegions?.cities || [])
        .filter((city) => city.offer_count > 0)
        .sort((a, b) => b.offer_count - a.offer_count || a.city.localeCompare(b.city, "pl"));
    const otherCities = citiesWithOffers.filter((city) => city.citySlug.toLowerCase() !== currentCitySlug).slice(0, 6);
    const regionSlug = locationRegions?.region?.toLowerCase();
    const regionName = regionSlug ? getVoivodeshipName(regionSlug as Voivodeship) || capitalizeFirst(regionSlug) : "";
    const regionHref = regionSlug
        ? buildListHref({
              offerType,
              type: makeSlug,
              model: modelSlug,
              location: regionSlug
          })
        : "";

    const cityLink = (city: City, region: string) => {
        const label = `Samochody używane${makeModelLabel ? ` ${makeModelLabel}` : ""} ${city.city} (${city.offer_count} ${pluralListing(city.offer_count)})`;
        return (
            <li key={`${region}-${city.citySlug}`}>
                <a
                    href={buildListHref({
                        offerType,
                        type: makeSlug,
                        model: modelSlug,
                        location: region.toLowerCase(),
                        subLocation: city.citySlug
                    })}
                    css={linkStyle}
                >
                    {label}
                </a>
            </li>
        );
    };

    return (
        <div>
            {locationRegions && currentCitySlug && otherCities.length + (regionHref ? 1 : 0) > 0 && (
                <div css={containerStyle}>
                    <h2 css={titleStyle}>Inne lokalizacje z ogłoszeniami</h2>
                    <div css={singleRegionContainer}>
                        {regionHref ? (
                            <a href={regionHref} css={linkStyle}>
                                {`Samochody używane${makeModelLabel ? ` ${makeModelLabel}` : ""} ${regionName}`}
                            </a>
                        ) : null}
                        {otherCities.length > 0 && <ul css={listStyle}>{otherCities.map((city) => cityLink(city, locationRegions.region))}</ul>}
                    </div>
                </div>
            )}
            {locationRegions && !currentCitySlug && citiesWithOffers.length > 0 && (
                <div css={containerStyle}>
                    <h2 css={titleStyle}>Miasta z ogłoszeniami</h2>
                    <ul css={listStyle}>{citiesWithOffers.map((city) => cityLink(city, locationRegions.region))}</ul>
                </div>
            )}
            {!parsed.location && topRegions && (
                <div css={containerStyle}>
                    <h2 css={titleStyle}>Sugerowane oferty z pobliskich lokalizacji</h2>
                    <div css={regionContainer}>
                        {Object.entries(topRegions).map(([region, cities]) => (
                            <div key={region} css={regionSectionStyle}>
                                <h3 css={regionTitleStyle}>
                                    <a
                                        href={buildListHref({
                                            offerType,
                                            type: makeSlug,
                                            model: modelSlug,
                                            location: region.toLowerCase()
                                        })}
                                    >
                                        {capitalizeFirst(getVoivodeshipName(region as Voivodeship))}
                                    </a>
                                </h3>
                                <ul css={listStyle}>{cities.filter((city) => city.offer_count > 0).map((city) => cityLink(city, region))}</ul>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const containerStyle = css`
    padding: 24px;
    display: flex;
    flex-direction: column;
`;
const singleRegionContainer = (theme: Theme) => css`
    display: flex;
    gap: 1rem;
    flex-direction: column;
`;

const regionContainer = (theme: Theme) => css`
    display: flex;
    flex-wrap: wrap;
    gap: 32px;

    flex: 1 1 100%;

    @media (min-width: ${theme.breakpoints.screen_md}) {
        flex: 1 1 23%;
        justify-content: space-between;
    }
`;

const titleStyle = css`
    font-size: 2.5rem;
    font-weight: 600;
    color: #333;
`;

const regionSectionStyle = css`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const regionTitleStyle = (theme: Theme) => css`
    font-size: 2rem;
    font-weight: 600;
    color: #333;
    a {
        color: inherit;
        text-decoration: none;
        &:hover {
            text-decoration: underline;
        }
    }
`;

const listStyle = css`
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const linkStyle = (theme: Theme) => css`
    color: ${theme.colors?.primary || "#2e79bd"};
    text-decoration: underline;
    font-size: 1.4rem;
    &:hover {
        opacity: 0.85;
    }
`;
